<template>
  <div>
    <EUIPageLoading v-if="appLoading" />
    <EUIApp :loading="loading || !theme" v-show="!appLoading" />
    <GlobalError />
    <GlobalDialog />
    <CalculatorDialog />
    <CalculatorDialogError />
  </div>
</template>

<script>
import { EUIApp, EUIPageLoading } from 'eclipx.ui';
import { mapGetters, mapMutations } from 'vuex';
import { isEmpty } from 'lodash';

import CalculatorDialog from './components/Calculator/CalculatorDialog';
import CalculatorDialogError from './components/Calculator/CalculatorDialogError';
import GlobalDialog from './components/Dialogs/GlobalDialog';
import GlobalError from './components/GlobalError/GlobalError';

import { onLogin } from './vue-apollo';

import QueryGetVehicleApplication from './graphql/queries/QueryGetVehicleApplication';
import QueryOrganisationProfiles from './graphql/queries/QueryOrganisationProfiles';
import QueryUser from './graphql/queries/QueryUser';
import { BUTTON_STYLE_ROUNDED, FONT_FAMILY_NUNITO, FONT_FAMILY_MMC } from './utils/constants';
import { setFavicon } from './utils/set-favicon';

import appConfig from './utils/app-config';

/**
 * This component handles fetching a user one the authentication service has finished requesting data.
 * The authentication service email will be used to query for the users details.
 *
 * Before a user is loaded, a full page loading screen will be displayed.
 */
export default {
  name: 'ScoreboardApp',
  data: function () {
    return {
      vehicleApplicationLoading: false,
      theme: false,
      domain: this.forLocalDomainConversion(window.location.hostname),
      au_DEFAULT_DOMAIN: appConfig.VUE_APP_AU_DEFAULT_DOMAIN ? appConfig.VUE_APP_AU_DEFAULT_DOMAIN : 'sme-customers1-au-uat.eclipx.xyz',
      nz_DEFAULT_DOMAIN: appConfig.VUE_APP_NZ_DEFAULT_DOMAIN ? appConfig.VUE_APP_NZ_DEFAULT_DOMAIN : 'sme-customers1-nz-uat.eclipx.xyz'
    };
  },
  computed: {
    user: function () {
      return this.$store.getters['auth/user'];
    },
    // Show an application loading state while the authentication service is in progress or there is no fetched
    // application user
    loading: function () {
      if (this.$route.meta.skipAuthentication) {
        return false;
      }

      return !this.user || this.$auth.loading || this.vehicleApplicationLoading;
    },
    vehicleApplicationStore: function () {
      return this.$store.getters['vehicleApplication/vehicleApplication'];
    },
    ...mapGetters({
      isUserPrimaryApplicant: 'vehicleApplication/isUserPrimaryApplicant',
      orgProfile: 'theme/getOrgProfile',
      appLoading: 'app/appLoading'
    })
  },
  watch: {
    // When this component first loads it is likely the authentication service is still fetching data.
    // Only call to get a user when the authentication service has finished
    '$auth.loading': function (loading) {
      if (!loading) {
        this.getUser();
      }
    }
  },
  created() {
    // Call API to get whitelabel details for subdomain
    this.getTheme().then((theme) => {
      this.theme = theme;
      document.documentElement.style.setProperty('--primary-color', theme.primary);
      document.documentElement.style.setProperty('--background-color', theme.background);
      document.documentElement.style.setProperty('--footer-color', theme.footer);
      document.documentElement.style.setProperty('--login-background', theme.loginimage);
      document.documentElement.style.setProperty('--brand-image', theme.brandimage);
      document.documentElement.style.setProperty('--primary-color-background', this.hexToRgb(theme.primary || '#004578'));

      const calculator = this.orgProfile?.calculator || {};
      const hasCalculatorWhiteLabeling = !!Object.keys(calculator).length;
      const isFleetPartnersDomain = [this.au_DEFAULT_DOMAIN, this.nz_DEFAULT_DOMAIN].includes(this.domain);

      const setIconGradient = (color1, color2) => {
        document.documentElement.style.setProperty('--icon-gradient-color1', color1);
        document.documentElement.style.setProperty('--icon-gradient-color2', color2);
      };

      if (!isFleetPartnersDomain && hasCalculatorWhiteLabeling) {
        setIconGradient(this.hexToRgb(theme.primary, 40, 0.6), this.hexToRgb(theme.primary, null, 1));
      } else {
        setIconGradient('#0f2d52', '#00aeaa');
      }

      document.documentElement.style.setProperty('--tertiary-color', theme.tertiary || '#EC7700');

      document.documentElement.style.setProperty(
        '--calculator-button-radius',
        !calculator?.buttonStyle || calculator?.buttonStyle === BUTTON_STYLE_ROUNDED ? '6px' : '0px'
      );

      document.documentElement.style.setProperty(
        '--calculator-group-button-radius',
        !calculator?.buttonStyle || calculator?.buttonStyle === BUTTON_STYLE_ROUNDED ? '40px' : '0px'
      );

      const fontChoice = calculator?.fontChoice || FONT_FAMILY_NUNITO;

      if (fontChoice === FONT_FAMILY_MMC) {
        document.documentElement.style.setProperty('--calculator-font-family', 'MMC, verdana, sans-serif');
      }

      if (calculator?.favicon) {
        setFavicon(calculator.favicon);
      }

      if (calculator?.loginBtnColour) {
        document.documentElement.style.setProperty('--calculator-login-btn-colour', calculator?.loginBtnColour);
      }
    });

    // If the authentication service has finished fetching data before the component was created, we can fetch a user
    if (!this.$auth.loading) {
      this.getUser();
    }

    if (this.$route.name != 'calculator' && isEmpty(this.vehicleApplicationStore)) {
      this.getVehicleApplication();
    }
  },
  methods: {
    ...mapMutations({
      setUser: 'auth/setUser',
      clearUser: 'auth/clearUser',
      setVehicleApplication: 'vehicleApplication/setVehicleApplication',
      clearVehicleApplication: 'vehicleApplication/clearVehicleApplication',
      setProfile: 'theme/setProfile',
      setCalculator: 'theme/setCalculator'
    }),
    hexToRgb(hex, percent = null, opacity = 0.1) {
      const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
      hex = hex.replace(shorthandRegex, function (m, r, g, b) {
        return r + r + g + g + b + b;
      });

      let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

      let R = parseInt(result[1], 16);
      let G = parseInt(result[2], 16);
      let B = parseInt(result[3], 16);

      if (percent !== null && !isNaN(percent)) {
        R = parseInt((R * (100 + percent)) / 100);
        G = parseInt((G * (100 + percent)) / 100);
        B = parseInt((B * (100 + percent)) / 100);

        R = R < 255 ? R : 255;
        G = G < 255 ? G : 255;
        B = B < 255 ? B : 255;

        R = Math.round(R);
        G = Math.round(G);
        B = Math.round(B);
      }

      return result ? `rgba(${R},${G},${B}, ${opacity})` : null;
    },
    forLocalDomainConversion(domain) {
      return domain.replace('.localhost', '.eclipx.xyz');
    },
    async getTheme() {
      var parts = window.location.hostname.split('.');
      var subdomain = 'default';
      if (parts.length == 3 && parts[0] != 'www' && parts[0] != 'fleetpartners-dev' && parts[0] != 'fleetpartners-uat') {
        subdomain = parts[0];
      }

      let themeAppQuery = await this.$apollo.query({
        query: QueryOrganisationProfiles,
        variables: {
          domain: this.domain
        }
      });

      var profiles = themeAppQuery.data?.getOrgProfilesByDomain;

      this.setProfile(profiles[0]);
      const calculator = this.parseCalculator(profiles[0]);

      this.setCalculator(calculator);
      let theme = profiles && profiles.length > 0 && profiles[0].scoreboard;
      var auth0 = profiles && profiles.length > 0 && profiles[0].auth0;

      if (this.$route.path.startsWith('/calculator')) {
        return {
          primary: calculator?.primary || '#002856',
          background: calculator?.secondary,
          footer: calculator?.primary,
          tertiary: calculator?.tertiary,
          brandimage: auth0?.primaryLogo || `url(${process.env.BASE_URL}assets/logos/fleetpartners_logo.svg)`
        };
      }

      if (theme) {
        /*
        return {
          primary: theme.darkColour,
          background: theme.lightColour,
          loginimage:
            'url(https://www-asia.nissan-cdn.net/content/dam/Nissan/AU/Images/vehicles/JUKE/overview/juke-nim-banner-3000x1200.jpg)',
          brandimage: 'url(https://www.carlogos.org/car-logos/nissan-logo-2020-black-show.png)'
        };*/

        return {
          primary: theme.primary,
          background: theme.secondary,
          footer: theme.background || theme.primary,
          loginimage: auth0?.backgroundImage || `url(${process.env.BASE_URL}assets/images/Quoting_bgimage.jpg)`,
          brandimage: auth0?.primaryLogo || `url(${process.env.BASE_URL}assets/logos/fleetpartners_logo.svg)`
        };
      } else {
        return {
          primary: '#030745',
          background: '#e9f3fd',
          footer: '#030745',
          loginimage: `url(${process.env.BASE_URL}assets/images/Quoting_bgimage.jpg)`,
          brandimage: `url(${process.env.BASE_URL}assets/logos/fleetpartners_logo.svg)`
        };
      }
    },
    async getUser() {
      // If the authentication is invalid, kick the user back to the login page
      if (!this.$auth.isAuthenticated) {
        return;
      }

      try {
        const token = await this.$auth.getIdTokenClaims();
        onLogin(this.$apollo, token.__raw);
        /*
        const token = await this.$auth.token;
        onLogin(this.$apollo, token);
        */

        // Do not fetch a user if the route does not require authentication
        // Do not fetch a user if there is one with the same email as the authentication service
        if (this.$route.meta.skipAuthentication || (this.user && this.user.email === this.$auth.user.email)) {
          return;
        }

        // Prevent the loading state from being removed while the new user request is in progress
        this.clearUser();

        // Fetch details of the authenticated user
        let query = await this.$apollo.query({
          query: QueryUser,
          variables: {
            email: this.$auth.user.email,
            appCode: appConfig.VUE_APP_CODE
          }
        });
        this.setUser(query.data.user);
        this.getVehicleApplication();
      } catch (e) {
        // eslint-disable-next-line
        console.log('getUser - error ', JSON.stringify({ e }));

        // Log out the authentication service if there was an error getting the user
        this.$router.push({ name: 'error' });
      }
    },
    async getVehicleApplication() {
      /*
      Demo for using orgProfiles by domain
      let orgProfiles = await this.$apollo.query({
        query: QueryOrganisationProfiles,
        variables: {
          domain: 'test-name1'
        }
      });
      */

      // If the authentication is invalid, kick the user back to the login page
      if (!this.user) {
        return;
      }
      this.vehicleApplicationLoading = true;
      try {
        this.clearVehicleApplication();

        // Fetch details of the vehicle Application
        let vehicleAppQuery = await this.$apollo.query({
          query: QueryGetVehicleApplication
        });
        let vehicleApplication = vehicleAppQuery?.data?.getVehicleApplication?.vehicleApplication
          ? vehicleAppQuery?.data?.getVehicleApplication?.vehicleApplication
          : null;
        // Canceled status check
        if (vehicleApplication?.scoreboard?.status === 'canceled') {
          // eslint-disable-next-line
          console.log('vehicleApplication - cancelled');

          this.$router.push({ name: 'dashboard' });
          // this.$router.push({
          //   name: 'error',
          //   params: { message: 'Your quote is no longer valid' }
          // });
        }

        // MLA validation
        if (
          vehicleApplication?.scoreboard?.validMLADate &&
          new Date(vehicleApplication.scoreboard.validMLADate).getTime() < new Date().getTime()
        ) {
          // eslint-disable-next-line
          console.log('vehicleApplication - mla outdated');

          this.$router.push({ name: 'dashboard' });
          // this.$router.push({
          //   name: 'error',
          //   params: { message: 'Your quote is no longer valid' }
          // });
        } else {
          this.setVehicleApplication(vehicleApplication);
          this.vehicleApplicationLoading = false;

          if (this.isUserPrimaryApplicant) {
            const useDashboard = vehicleApplication.applicationType === 'calculator';
            // eslint-disable-next-line
            console.log(
              'vehicleApplication - primary applicant',
              useDashboard ? '(calculator → dashboard)' : '(applications / CRM scoreboard or non-calculator)'
            );

            if (useDashboard) {
              this.$router.push({ name: 'dashboard' });
            } else {
              this.$router.push({
                name: 'applications',
                params: { applicationId: vehicleApplication.applicationId }
              });
            }
          } else {
            this.$router.push({ name: 'applications', params: { applicationId: vehicleApplication.applicationId } });
          }
        }
      } catch (e) {
        // eslint-disable-next-line
        console.log('vehicleApplication - error ', JSON.stringify({ e }));

        // Log out the authentication service if there was an error getting the user
        this.vehicleApplicationLoading = false;
        this.$router.push({ name: 'error' });
      }
    },
    parseCalculator(profile) {
      //Default value
      if (!profile.calculator || Object.keys(profile.calculator).length === 0) {
        const url = profile?.whiteLabel?.region === 'NZ' ? 'https://fleetpartners.co.nz/' : 'https://fleetpartners.com.au/';
        return {
          secondary: '#00aeaa',
          footerText: '',
          footerColor: '',
          headerImage: 'https://assets.eclipx.xyz/sme/dev/de73f485-dcbb-4807-bec0-2ef46a61bc34',
          tertiary: '#f0540c',
          headerLinks: {
            menu_3: {
              menuUrl: `${url}contact-us`,
              menuTitle: 'Contact Us'
            },
            menu_2: {
              menuUrl: `${url}about`,
              menuTitle: 'About'
            },
            menu_1: {
              menuUrl: url,
              menuTitle: 'Home'
            }
          },
          disclaimerText: '',
          headerColour: '#002856',
          primary: '#002856'
        };
      }
      if (profile.calculator?.headerLinks && Object.keys(profile.calculator?.headerLinks).length > 1) {
        profile.calculator.headerLinks = Object.keys(profile.calculator.headerLinks)
          .map((key) => {
            const sortOrder = key.split('menu_')[1] || 0;
            return {
              sortOrder,
              menuTitle: profile.calculator.headerLinks[key]?.menuTitle || '',
              menuUrl: profile.calculator.headerLinks[key]?.menuUrl || ''
            };
          })
          .sort((a, b) => {
            return a.sortOrder - b.sortOrder;
          });
        // profile.calculator.headerLinks
      }

      return profile?.calculator;
    }
  },
  components: {
    EUIApp,
    EUIPageLoading,
    GlobalError,
    GlobalDialog,
    CalculatorDialog,
    CalculatorDialogError
  }
};
</script>
